import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Trash2, ChevronDown } from "lucide-react";
import { Markdown } from "@/components/Markdown";
import { PremiumGate } from "@/components/PremiumGate";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// Reusable AI research panel for both health & behaviour
export const ResearchPanel = ({ kind, dogs, defaultDog = "", defaultQuery = "", placeholder, extraLabel, extraPlaceholder }) => {
  const [dogId, setDogId] = useState(defaultDog);
  const [query, setQuery] = useState(defaultQuery);
  const [extra, setExtra] = useState("");
  const [model, setModel] = useState("gemini");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [openId, setOpenId] = useState(null);
  const resultRef = useRef(null);

  const loadHistory = () => api.get(`/ai/research/history?kind=${kind}`).then((r) => setHistory(r.data));
  useEffect(() => { loadHistory(); }, []); // eslint-disable-line

  const run = async () => {
    if (!query.trim()) { toast.error("Describe what you're seeing first"); return; }
    setLoading(true); setResult(null);
    if (window.innerWidth < 1024) resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    try {
      const r = await api.post("/ai/research", { dog_id: dogId || null, kind, query, extra, model });
      setResult(r.data.result); loadHistory();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("Budget") || detail.includes("budget")) {
        toast.error("AI credits exhausted. Add balance to your Universal Key (Profile → Universal Key → Add Balance).");
      } else {
        toast.error("AI research failed. Please try again.");
      }
    }
    setLoading(false);
  };

  return (
    <PremiumGate feature="AI research">
    <div className="grid lg:grid-cols-5 gap-6 items-start">
      {/* Input form — mobile: first */}
      <div className="order-1 lg:col-span-2 lg:col-start-1 lg:row-start-1 min-w-0">
        <div className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm">
          <div className="space-y-3">
            <div>
              <Label>Which dog?</Label>
              <select data-testid="research-dog" value={dogId} onChange={(e) => setDogId(e.target.value)} className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Not specified</option>
                {dogs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <Label>{placeholder.label}</Label>
              <Textarea data-testid="research-query" rows={4} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder.text} />
            </div>
            {extraLabel && (
              <div><Label>{extraLabel}</Label><Textarea data-testid="research-extra" rows={2} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder={extraPlaceholder} /></div>
            )}
            <div>
              <Label>AI model</Label>
              <div className="flex gap-2 mt-1">
                <button data-testid="model-gemini" onClick={() => setModel("gemini")} className={`flex-1 min-w-0 rounded-lg border px-3 py-2 text-sm font-medium ${model === "gemini" ? "border-primary bg-primary/10" : "border-border"}`}>Gemini 3.1 Pro</button>
                <button data-testid="model-openai" onClick={() => setModel("openai")} className={`flex-1 min-w-0 rounded-lg border px-3 py-2 text-sm font-medium ${model === "openai" ? "border-primary bg-primary/10" : "border-border"}`}>GPT-5.5</button>
              </div>
            </div>
            <Button data-testid="run-research" onClick={run} disabled={loading} className="w-full rounded-full">
              {loading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Researching...</> : <><Sparkles className="h-4 w-4 mr-1" /> Research now</>}
            </Button>
          </div>
        </div>
      </div>

      {/* AI findings — mobile: second (right after the form) */}
      <div ref={resultRef} className="order-2 lg:order-none lg:col-span-3 lg:col-start-3 lg:row-start-1 lg:row-span-2 min-w-0">
        <div className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm min-h-[20rem]">
          <div className="flex items-center gap-2 mb-4"><Sparkles className="h-5 w-5 text-accent-foreground" /><h3 className="font-head font-semibold">AI findings</h3></div>
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div key="load" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center h-72 text-muted-foreground text-center px-4">
                <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ repeat: Infinity, duration: 1.4 }}><Sparkles className="h-8 w-8 text-accent mb-3" /></motion.div>
                Searching vet sources, publications & forums...
              </motion.div>
            ) : result ? (
              <motion.div key="res" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}><Markdown content={result} /></motion.div>
            ) : (
              <motion.div key="empty" className="flex flex-col items-center justify-center h-72 text-center text-muted-foreground px-4">
                <Sparkles className="h-8 w-8 mb-3 text-muted-foreground/40" />
                <p>Describe the {kind === "health" ? "symptoms" : "behaviour"} and hit <b>Research now</b>.<br />AI will surface possible causes and how to handle them.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Past research — mobile: last */}
      {history.length > 0 && (
        <div className="order-3 lg:order-none lg:col-span-2 lg:col-start-1 lg:row-start-2 min-w-0">
          <div className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm">
            <h3 className="font-head font-semibold mb-3">Past research</h3>
            <div className="space-y-2 max-h-80 overflow-y-auto scroll-thin">
              {history.map((h) => (
                <div key={h.id} className="rounded-xl border border-border/60 min-w-0">
                  <button onClick={() => setOpenId(openId === h.id ? null : h.id)} className="w-full flex items-center justify-between px-3 py-2 text-left text-sm">
                    <span className="truncate pr-2">{h.query}</span>
                    <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${openId === h.id ? "rotate-180" : ""}`} />
                  </button>
                  {openId === h.id && (
                    <div className="px-3 pb-3">
                      <Markdown content={h.result} />
                      <button onClick={async () => { await api.delete(`/ai/research/${h.id}`); loadHistory(); }} className="mt-2 text-xs text-destructive flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </PremiumGate>
  );
};
